import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router";
import { CSSTransition, TransitionGroup } from "react-transition-group";
import { Text } from "components/designV2/text/text";
import { CompanyRequestDto } from "model/Company";
import firebase from "services/firebase/firebase.config";
import { useAuthStore } from "stores/authStore/authStore";
import { useCompanyStore } from "web-apps/company/stores/companyStore/companyStore";
import { CompanyInfoForm } from "../companyInfoForm/companyInfoForm.component";
import { Confirmation } from "../confirmation/confirmation.component";
import { ContactForm } from "../contactForm/contactForm.component";
import { CreateProfile } from "../createProfile/createProfile.component";
import { createCompanyRegistrationRequest } from "./registrationContainer.logic";
import { Slide, PageSliderStyled } from "./registrationContainer.styled";

export const RegistrationContainer = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const firebaseUser = firebase.auth().currentUser;
  const [authState, authActions] = useAuthStore();
  const [companyState, companyActions] = useCompanyStore();
  const [page, setPage] = useState(0);
  const [companyName, setCompanyName] = useState("");
  const [registrationNumber, setRegistrationNumber] = useState("");
  const [legalName, setLegalName] = useState("");
  const [contactPersonEmail, setContactPersonEmail] = useState("");
  const [contactPersonPhoneNumber, setContactPersonPhoneNumber] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [companyBio, setCompanyBio] = useState("");
  const [city, setCity] = useState("");
  const [companyAdress, setCompanyAdress] = useState("");
  const [postalCode, setPostalCode] = useState("");
  const [location, setLocation] = useState("");
  const [error, setError] = useState(false);
  const [prevPage, setPrevPage] = useState(0);

  const register = async (): Promise<void> => {
    const hasRequiredCompanyData = Boolean(
      companyName &&
      registrationNumber &&
      legalName &&
      contactPersonEmail &&
      contactPersonPhoneNumber &&
      firstName &&
      lastName &&
      city &&
      companyAdress &&
      postalCode &&
      location,
    );

    if (!hasRequiredCompanyData) {
      return Promise.reject(new Error("Missing company registration data"));
    }

    const data = createCompanyRegistrationRequest({
      city,
      companyAdress,
      companyBio,
      companyName,
      contactPersonEmail,
      contactPersonPhoneNumber,
      firebaseEmail: firebaseUser?.email ?? undefined,
      firstName,
      lastName,
      legalName,
      location,
      postalCode,
      registrationNumber,
    });

    return companyActions
      .saveCompany(data, companyState.companies)
      .then(() => {
        setPage((prevState) => prevState + 1);
      })
      .catch(() => {
        setError(true);
        return Promise.reject();
      });
  };

  const updateCompanyDescription = async (): Promise<void> => {
    if (!companyState.company) {
      return;
    }

    const data: CompanyRequestDto = {
      ...companyState.company,
      description: companyBio,
    };

    return companyActions
      .saveCompany(data, companyState.companies, companyState.company.id)
      .then(() => {
        setPage((prevState) => prevState + 1);
      })
      .catch(() => {
        setError(true);
        return Promise.reject();
      });
  };

  const handleLogin = async (): Promise<void> => {
    if (!authState.firestoreUser) {
      setError(true);
      return Promise.reject(new Error("Missing authenticated user"));
    }

    return authActions
      .loginEmailAndPassword(authState.firestoreUser)
      .then(() => {
        history.push("/company/schedule");
      })
      .catch(() => {
        setError(true);
        return Promise.reject();
      });
  };

  const renderSlide = () => {
    switch (page) {
      case 0: {
        return (
          <CompanyInfoForm
            page={page}
            setPage={setPage}
            companyName={companyName}
            setCompanyName={setCompanyName}
            registrationNumber={registrationNumber}
            setRegistrationNumber={setRegistrationNumber}
            legalName={legalName}
            setLegalName={setLegalName}
            postalCode={postalCode}
            setPostalCode={setPostalCode}
            city={city}
            setCity={setCity}
            companyAdress={companyAdress}
            setCompanyAdress={setCompanyAdress}
            location={location}
            setLocation={setLocation}
          />
        );
      }
      case 1: {
        return (
          <ContactForm
            page={page}
            setPage={setPage}
            firstName={firstName}
            setFirstName={setFirstName}
            lastName={lastName}
            setLastName={setLastName}
            contactPersonEmail={contactPersonEmail}
            contactPersonPhoneNumber={contactPersonPhoneNumber}
            setContactPersonPhoneNumber={setContactPersonPhoneNumber}
            register={register}
          />
        );
      }
      case 2: {
        return (
          <CreateProfile
            companyBio={companyBio}
            setCompanyBio={setCompanyBio}
            updateCompanyDescription={updateCompanyDescription}
            page={page}
            setPage={setPage}
          />
        );
      }
      case 3: {
        return <Confirmation handleLogin={handleLogin} />;
      }
    }
  };

  useEffect(() => {
    if (firebaseUser?.email) {
      setContactPersonEmail(firebaseUser.email);
    }
  }, [firebaseUser]);

  useEffect(() => {
    setPrevPage(page);

    const bodyEl = document.querySelector("body");
    if (bodyEl) bodyEl.scrollIntoView();
  }, [page]);

  return (
    <>
      {!error ? (
        <TransitionGroup
          component={PageSliderStyled}
          childFactory={(child) =>
            React.cloneElement(child, {
              classNames: `${
                prevPage < page ? "left" : prevPage > page ? "right" : "_"
              } slide`,
            })
          }
        >
          <CSSTransition
            key={page}
            timeout={{ enter: 600, exit: 600 }}
            mountOnEnter={false}
            unmountOnExit={true}
          >
            <Slide className="page">{renderSlide()}</Slide>
          </CSSTransition>
        </TransitionGroup>
      ) : (
        <PageSliderStyled>
          <Slide>
            <Text typography="b2">
              {t("RegisterCompany.SomethingWentWrong")}
            </Text>
          </Slide>
        </PageSliderStyled>
      )}
    </>
  );
};
